import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  LocalOffer as LabelIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { ARTWORKS_LIST_KEY } from '../utils/listViewState';
import { usePersistedListView } from '../hooks/usePersistedListView';
import CollectionSelect from '../components/CollectionSelect';
import ArtworkTagsEditor from '../components/ArtworkTagsEditor';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

export default function ArtworksList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchInput,
    setSearchInput,
    search,
    collectionFilter,
    setCollectionFilter,
    statusFilter,
    setStatusFilter,
    viewMode,
    setViewMode,
    persistBeforeLeave,
    restoreScroll,
    handleSearchClick,
  } = usePersistedListView(ARTWORKS_LIST_KEY);

  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [reportMenuArtwork, setReportMenuArtwork] = useState(null);

  const { data, isLoading } = useQuery(
    ['artworks', page, rowsPerPage, search, collectionFilter, statusFilter],
    async () => {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      if (search) params.search = search;
      if (collectionFilter) params.collection_code = collectionFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get('/api/artworks', { params });
      return response.data;
    }
  );

  useEffect(() => {
    restoreScroll(isLoading);
  }, [isLoading, data, restoreScroll]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'на хранении': 'default',
      'в экспозиции': 'primary',
      'на реставрации': 'info',
      'потерянно': 'error',
      'продано': 'success',
      'возвращено': 'warning',
      'исключено': 'error',
    };
    return colors[status] || 'default';
  };

  const handleOpenReportMenu = (artwork, event) => {
    setReportMenuArtwork(artwork);
    setReportMenuAnchor(event.currentTarget);
  };

  const handleCloseReportMenu = () => {
    setReportMenuAnchor(null);
    setReportMenuArtwork(null);
  };

  const handleExportReport = async (artwork, format) => {
    try {
      const response = await axios.get(`/api/artworks/${artwork.id}/legend?format=${format}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = { txt: '.txt', docx: '.docx', html: '.html', json: '.json' }[format];
      link.setAttribute('download', `legend_${artwork.inventory_number}${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Ошибка экспорта отчёта:', err);
    }
    handleCloseReportMenu();
  };

  const handleOpenLabel = async (artwork) => {
    try {
      const response = await axios.get(`/api/artworks/${artwork.id}/label`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (!blob.size) {
        throw new Error('Пустой файл');
      }
      const url = window.URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = `label_${artwork.inventory_number || artwork.id}.pdf`;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Ошибка генерации этикетки:', err);
    }
  };

  const goToArtwork = (id) => {
    persistBeforeLeave();
    navigate(`/artworks/${id}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <h2 style={{ margin: 0 }}>Произведения</h2>
        <Box display="flex" alignItems="center" gap={1}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val != null && setViewMode(val)}
            size="small"
            aria-label="вид отображения"
          >
            <ToggleButton value="table" aria-label="таблица">
              <ViewListIcon sx={{ mr: 0.5 }} /> Таблица
            </ToggleButton>
            <ToggleButton value="cards" aria-label="карточки">
              <ViewModuleIcon sx={{ mr: 0.5 }} /> Карточки
            </ToggleButton>
          </ToggleButtonGroup>
          {hasPermission(user, 'create_artworks') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              persistBeforeLeave();
              navigate('/artworks/new');
            }}
          >
            Добавить произведение
          </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              fullWidth
              label="Поиск"
              variant="outlined"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
              placeholder="Поиск… Слова через +, теги с # (напр. #масло)"
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearchClick}
              sx={{ minWidth: 100, flexShrink: 0 }}
            >
              Поиск
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <CollectionSelect
            allowEmpty
            value={collectionFilter}
            onChange={(e) => setCollectionFilter(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Статус</InputLabel>
            <Select
              value={statusFilter}
              label="Статус"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="на хранении">На хранении</MenuItem>
              <MenuItem value="в экспозиции">В экспозиции</MenuItem>
              <MenuItem value="на реставрации">На реставрации</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {viewMode === 'table' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Инв. номер</TableCell>
                <TableCell>Автор</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Коллекция</TableCell>
                <TableCell>Год создания</TableCell>
                <TableCell>Теги</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.artworks?.map((artwork) => (
                <TableRow key={artwork.id} hover>
                  <TableCell>{artwork.inventory_number}</TableCell>
                  <TableCell>{artwork.author?.full_name || 'Неизвестно'}</TableCell>
                  <TableCell>{artwork.artwork_title}</TableCell>
                  <TableCell>{artwork.collection_code}</TableCell>
                  <TableCell>{artwork.creation_year_display || artwork.creation_year || '—'}</TableCell>
                  <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>
                    <ArtworkTagsEditor
                      artworkId={artwork.id}
                      tags={artwork.tags}
                      editable={hasPermission(user, 'edit_artworks')}
                      compact
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={artwork.status}
                      color={getStatusColor(artwork.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        onClick={() => goToArtwork(artwork.id)}
                      >
                        Открыть
                      </Button>
                      {hasPermission(user, 'export_documents') && (
                        <>
                      <Tooltip title="Отчёт по произведению">
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenReportMenu(artwork, e)}
                          aria-label="Скачать отчёт"
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Этикетка для печати">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenLabel(artwork)}
                          aria-label="Скачать этикетку PDF"
                        >
                          <LabelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={reportMenuAnchor}
        open={Boolean(reportMenuAnchor)}
        onClose={handleCloseReportMenu}
      >
        <MenuItem onClick={() => reportMenuArtwork && handleExportReport(reportMenuArtwork, 'txt')}>TXT</MenuItem>
        <MenuItem onClick={() => reportMenuArtwork && handleExportReport(reportMenuArtwork, 'docx')}>DOCX</MenuItem>
        <MenuItem onClick={() => reportMenuArtwork && handleExportReport(reportMenuArtwork, 'html')}>HTML</MenuItem>
        <MenuItem onClick={() => reportMenuArtwork && handleExportReport(reportMenuArtwork, 'json')}>JSON</MenuItem>
      </Menu>

      {viewMode === 'cards' && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {data?.artworks?.map((artwork) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={artwork.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 4 },
                }}
              >
                {artwork.primary_image?.file_path ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={`/uploads/${artwork.primary_image.file_path}`}
                    alt={artwork.artwork_title}
                    sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Нет фото
                    </Typography>
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {artwork.inventory_number}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom noWrap title={artwork.artwork_title}>
                    {artwork.artwork_title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {artwork.author?.full_name || 'Неизвестно'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Коллекция: {artwork.collection_code} · {artwork.creation_year_display || artwork.creation_year || '—'}
                  </Typography>
                  <Chip
                    label={artwork.status}
                    color={getStatusColor(artwork.status)}
                    size="small"
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <ArtworkTagsEditor
                    artworkId={artwork.id}
                    tags={artwork.tags}
                    editable={hasPermission(user, 'edit_artworks')}
                    compact
                  />
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => goToArtwork(artwork.id)}>
                    Открыть
                  </Button>
                  {hasPermission(user, 'export_documents') && (
                    <>
                  <Tooltip title="Отчёт (TXT, DOCX, HTML, JSON)">
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenReportMenu(artwork, e)}
                      aria-label="Скачать отчёт"
                    >
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                      <Tooltip title="Этикетка для печати">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenLabel(artwork)}
                          aria-label="Скачать этикетку PDF"
                        >
                          <LabelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        labelRowsPerPage="Записей на странице:"
      />
    </Box>
  );
}

